import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Activity, Flame, Snowflake, ThermometerSun, MapPin, Monitor, Smartphone,
  ChevronRight, ArrowLeft, Phone, CalendarCheck, MousePointerClick, Eye, Film, ListChecks,
  Users, UserCheck, Target, Zap,
} from "lucide-react";
import {
  classifyIntent, classifySegment, recommendedAction, intentColor, segmentLabel,
  type VisitorEventLite,
} from "@/lib/visitorIntel";

interface Visitor {
  visitor_id: string;
  first_seen: string;
  last_seen: string;
  visit_count: number;
  country: string | null;
  region: string | null;
  city: string | null;
  device: string | null;
  browser: string | null;
  referrer: string | null;
  landing_page: string | null;
  utm_source: string | null;
  total_events: number;
  total_page_views: number;
  engagement_score: number;
  temperature: "hot" | "warm" | "cold";
}
interface VisitorEvent extends VisitorEventLite {
  id: string;
  visitor_id: string;
  session_id: string;
  metadata: Record<string, unknown>;
  country: string | null;
  city: string | null;
  device: string | null;
  created_at: string;
  path: string | null;
  label: string | null;
}

const tempColor = (t: string) =>
  t === "hot" ? "hsl(0 75% 60%)" : t === "warm" ? "hsl(35 85% 60%)" : "hsl(207 30% 55%)";
const tempIcon = (t: string) =>
  t === "hot" ? <Flame size={12} /> : t === "warm" ? <ThermometerSun size={12} /> : <Snowflake size={12} />;

const eventIcon = (type: string) => {
  if (type.startsWith("call_")) return <Phone size={14} />;
  if (type.startsWith("book_") || type.startsWith("booking_")) return <CalendarCheck size={14} />;
  if (type.startsWith("video_")) return <Film size={14} />;
  if (type.startsWith("faq_")) return <ListChecks size={14} />;
  if (type === "scroll_milestone") return <MousePointerClick size={14} />;
  return <Eye size={14} />;
};
const eventLabel = (e: VisitorEvent) => {
  const friendly: Record<string, string> = {
    page_viewed: "Viewed page",
    session_started: "Started session",
    service_page_viewed: "Viewed service",
    booking_page_viewed: "Viewed booking page",
    blog_article_viewed: "Read article",
    book_call_clicked: "Clicked Book Call",
    call_link_clicked: "Clicked phone",
    email_link_clicked: "Clicked email",
    booking_form_submitted: "Submitted booking",
    lead_form_submitted: "Submitted lead form",
    lead_form_started: "Started lead form",
    case_study_viewed: "Viewed case study",
    video_started: "Started video",
    video_completed: "Completed video",
    faq_opened: "Opened FAQ",
    faq_closed: "Closed FAQ",
    scroll_milestone: "Scrolled",
  };
  return friendly[e.event_type] || e.event_type;
};
const timeAgo = (iso: string) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const cardBg = "hsl(0 0% 8%)";
const cardBorder = "hsl(0 0% 16%)";
const textMuted = "hsl(0 0% 55%)";
const textHi = "hsl(0 0% 92%)";

type FeedFilter = "all" | "high_intent" | "bookings" | "calls" | "services" | "returning";

const matchesFeed = (e: VisitorEvent, f: FeedFilter, returningIds: Set<string>): boolean => {
  if (f === "all") return true;
  if (f === "bookings") return e.event_type === "book_call_clicked" || e.event_type.startsWith("booking_");
  if (f === "calls") return e.event_type === "call_link_clicked";
  if (f === "services") return e.event_type === "service_page_viewed";
  if (f === "returning") return returningIds.has(e.visitor_id);
  if (f === "high_intent") {
    return [
      "book_call_clicked", "call_link_clicked",
      "booking_page_viewed", "booking_form_submitted",
      "lead_form_submitted",
    ].includes(e.event_type);
  }
  return true;
};

export default function VisitorIntelligenceTab() {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [feed, setFeed] = useState<VisitorEvent[]>([]);
  const [filter, setFilter] = useState<"all" | "hot" | "warm" | "cold">("all");
  const [feedFilter, setFeedFilter] = useState<FeedFilter>("all");
  const [selected, setSelected] = useState<Visitor | null>(null);
  const [timeline, setTimeline] = useState<VisitorEvent[]>([]);
  const [hotEventsByVisitor, setHotEventsByVisitor] = useState<Record<string, VisitorEvent[]>>({});
  const [loading, setLoading] = useState(true);

  // Initial load
  useEffect(() => {
    (async () => {
      const [v, e] = await Promise.all([
        supabase.from("visitors").select("*").order("last_seen", { ascending: false }).limit(200),
        supabase.from("visitor_events").select("*").order("created_at", { ascending: false }).limit(80),
      ]);
      if (v.data) setVisitors(v.data as Visitor[]);
      if (e.data) setFeed(e.data as VisitorEvent[]);
      setLoading(false);
    })();
  }, []);

  // Visitors/events excluded from Realtime for PII protection. Poll while visible.
  useEffect(() => {
    const reload = async () => {
      const [v, e] = await Promise.all([
        supabase.from("visitors").select("*").order("last_seen", { ascending: false }).limit(200),
        supabase.from("visitor_events").select("*").order("created_at", { ascending: false }).limit(80),
      ]);
      if (v.data) setVisitors(v.data as Visitor[]);
      if (e.data) setFeed(e.data as VisitorEvent[]);
    };
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Fetch event histories for HOT visitors so the action center can classify
  const hotVisitors = useMemo(() => visitors.filter((v) => v.temperature === "hot"), [visitors]);
  useEffect(() => {
    if (hotVisitors.length === 0) return;
    const missing = hotVisitors.filter((v) => !hotEventsByVisitor[v.visitor_id]).map((v) => v.visitor_id);
    if (missing.length === 0) return;
    (async () => {
      const { data } = await supabase
        .from("visitor_events")
        .select("visitor_id, event_type, path, label, created_at")
        .in("visitor_id", missing)
        .order("created_at", { ascending: false })
        .limit(500);
      if (!data) return;
      const grouped: Record<string, VisitorEvent[]> = { ...hotEventsByVisitor };
      for (const id of missing) grouped[id] = [];
      for (const row of data) {
        const arr = grouped[row.visitor_id] || (grouped[row.visitor_id] = []);
        arr.push(row as VisitorEvent);
      }
      setHotEventsByVisitor(grouped);
    })();
  }, [hotVisitors, hotEventsByVisitor]);

  // Visitor detail timeline
  useEffect(() => {
    if (!selected) return setTimeline([]);
    (async () => {
      const { data } = await supabase
        .from("visitor_events")
        .select("*").eq("visitor_id", selected.visitor_id)
        .order("created_at", { ascending: false }).limit(300);
      if (data) setTimeline(data as VisitorEvent[]);
    })();
  }, [selected]);

  // Summary stats
  const stats = useMemo(() => {
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const isToday = (d: string) => new Date(d).getTime() >= todayStart.getTime();
    const todayVisitors = visitors.filter((v) => isToday(v.last_seen));
    const todayFeed = feed.filter((e) => isToday(e.created_at));
    const avgScore = visitors.length
      ? Math.round(visitors.reduce((s, v) => s + v.engagement_score, 0) / visitors.length)
      : 0;
    return {
      todayVisitors: todayVisitors.length,
      hot: visitors.filter((v) => v.temperature === "hot").length,
      returning: visitors.filter((v) => v.visit_count >= 2).length,
      bookingPv: todayFeed.filter((e) => e.event_type === "booking_page_viewed").length,
      phoneClicks: todayFeed.filter((e) => e.event_type === "call_link_clicked").length,
      bookClicks: todayFeed.filter((e) => e.event_type === "book_call_clicked").length,
      avgScore,
    };
  }, [visitors, feed]);

  const returningIds = useMemo(
    () => new Set(visitors.filter((v) => v.visit_count >= 2).map((v) => v.visitor_id)),
    [visitors],
  );
  const filteredVisitors = filter === "all" ? visitors : visitors.filter((v) => v.temperature === filter);
  const filteredFeed = feed.filter((e) => matchesFeed(e, feedFilter, returningIds)).slice(0, 50);
  const counts = {
    hot: visitors.filter((v) => v.temperature === "hot").length,
    warm: visitors.filter((v) => v.temperature === "warm").length,
    cold: visitors.filter((v) => v.temperature === "cold").length,
  };

  // ====== DETAIL VIEW ======
  if (selected) {
    const intentResult = classifyIntent(timeline);
    const segment = classifySegment(timeline);
    const action = recommendedAction(intentResult.intent, selected, intentResult.reasons);
    return (
      <div>
        <button onClick={() => setSelected(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm" style={{ color: textMuted }}>
          <ArrowLeft size={14} /> Back to all visitors
        </button>

        <div className="rounded-xl border p-6 mb-6" style={{ background: cardBg, borderColor: cardBorder }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Pill color={tempColor(selected.temperature)} icon={tempIcon(selected.temperature)}>
                  {selected.temperature.toUpperCase()}
                </Pill>
                <Pill color={intentColor(intentResult.intent)} icon={<Target size={11} />}>
                  {intentResult.intent.toUpperCase()} INTENT
                </Pill>
                <Pill color="hsl(207 68% 60%)" icon={<UserCheck size={11} />}>
                  {segmentLabel[segment]}
                </Pill>
                <span className="text-xs font-mono" style={{ color: textMuted }}>{selected.visitor_id}</span>
              </div>
              <h2 className="text-xl font-bold" style={{ color: textHi }}>
                {[selected.city, selected.region, selected.country].filter(Boolean).join(", ") || "Unknown location"}
              </h2>
              <div className="mt-2 flex flex-wrap gap-3 text-xs" style={{ color: textMuted }}>
                {selected.device === "mobile" ? <Smartphone size={12} /> : <Monitor size={12} />}
                <span>{selected.device} · {selected.browser}</span>
                <span>· First seen {timeAgo(selected.first_seen)}</span>
                <span>· {selected.visit_count} visit{selected.visit_count !== 1 ? "s" : ""}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold" style={{ color: tempColor(selected.temperature) }}>{selected.engagement_score}</div>
              <div className="text-xs uppercase tracking-wider" style={{ color: textMuted }}>Engagement</div>
            </div>
          </div>

          {/* Action center */}
          <div className="mt-5 rounded-lg border p-4" style={{ borderColor: cardBorder, background: "hsl(0 0% 6%)" }}>
            <div className="flex items-center gap-2 mb-2">
              <Zap size={14} style={{ color: "hsl(35 85% 60%)" }} />
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: textHi }}>Why flagged</span>
            </div>
            <ul className="text-sm space-y-1 mb-3" style={{ color: textMuted }}>
              {intentResult.reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-1 w-1 rounded-full" style={{ background: intentColor(intentResult.intent) }} />
                  <span style={{ color: "hsl(0 0% 75%)" }}>{r}</span>
                </li>
              ))}
            </ul>
            <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: textHi }}>Recommended action</div>
            <p className="text-sm" style={{ color: "hsl(207 68% 70%)" }}>{action}</p>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <Stat label="Page views" value={selected.total_page_views} />
            <Stat label="Total events" value={selected.total_events} />
            <Stat label="Landing" value={selected.landing_page || ", "} />
            <Stat label="Source" value={selected.utm_source || selected.referrer || "direct"} />
          </div>
        </div>

        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: textMuted }}>Timeline</h3>
        <div className="space-y-2">
          {timeline.map((e) => (
            <div key={e.id} className="flex items-start gap-3 rounded-lg border p-3" style={{ background: cardBg, borderColor: cardBorder }}>
              <div className="mt-0.5" style={{ color: "hsl(207 68% 60%)" }}>{eventIcon(e.event_type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 text-sm">
                  <span className="font-medium" style={{ color: textHi }}>{eventLabel(e)}</span>
                  {e.path && <span className="text-xs font-mono" style={{ color: textMuted }}>{e.path}</span>}
                </div>
                {e.label && <div className="text-xs mt-0.5" style={{ color: textMuted }}>{e.label}</div>}
              </div>
              <div className="text-xs whitespace-nowrap" style={{ color: textMuted }}>
                {new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          ))}
          {timeline.length === 0 && <p className="text-sm" style={{ color: textMuted }}>No events yet.</p>}
        </div>
      </div>
    );
  }

  // ====== LIST VIEW ======
  return (
    <div>
      {/* SUMMARY CARDS */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <SummaryCard label="Visitors Today" value={stats.todayVisitors} icon={<Users size={14} />} />
        <SummaryCard label="Hot Visitors" value={stats.hot} icon={<Flame size={14} />} accent="hsl(0 75% 60%)" />
        <SummaryCard label="Returning" value={stats.returning} icon={<UserCheck size={14} />} />
        <SummaryCard label="Booking PVs" value={stats.bookingPv} icon={<CalendarCheck size={14} />} />
        <SummaryCard label="Phone Clicks" value={stats.phoneClicks} icon={<Phone size={14} />} />
        <SummaryCard label="Book CTA" value={stats.bookClicks} icon={<MousePointerClick size={14} />} />
        <SummaryCard label="Avg. Score" value={stats.avgScore} icon={<Target size={14} />} />
      </div>

      {/* HOT VISITOR ACTION STRIP */}
      {hotVisitors.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: textHi }}>
            <Flame size={14} style={{ color: "hsl(0 75% 60%)" }} /> Owner Action Center
          </h3>
          <div className="grid gap-3 md:grid-cols-2">
            {hotVisitors.slice(0, 4).map((v) => {
              const evs = hotEventsByVisitor[v.visitor_id] || [];
              const { intent, reasons } = classifyIntent(evs);
              const seg = classifySegment(evs);
              return (
                <button key={v.visitor_id} onClick={() => setSelected(v)}
                  className="text-left rounded-xl border p-4 transition-colors hover:border-[hsl(0_75%_60%/0.5)]"
                  style={{ background: cardBg, borderColor: "hsl(0 75% 60% / 0.3)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Pill color="hsl(0 75% 60%)" icon={<Flame size={11} />}>HOT</Pill>
                      <Pill color={intentColor(intent)} icon={<Target size={11} />}>{intent.toUpperCase()}</Pill>
                      <Pill color="hsl(207 68% 60%)" icon={<UserCheck size={11} />}>{segmentLabel[seg]}</Pill>
                    </div>
                    <span className="text-xs" style={{ color: textMuted }}>{timeAgo(v.last_seen)}</span>
                  </div>
                  <div className="text-sm font-medium mb-1" style={{ color: textHi }}>
                    {[v.city, v.region, v.country].filter(Boolean).join(", ") || "Unknown location"} · {v.visit_count} visit{v.visit_count !== 1 ? "s" : ""}
                  </div>
                  <ul className="text-xs space-y-0.5" style={{ color: textMuted }}>
                    {reasons.slice(0, 3).map((r, i) => <li key={i}>• {r}</li>)}
                  </ul>
                  <div className="mt-2 text-xs font-medium" style={{ color: "hsl(35 85% 65%)" }}>
                    → {recommendedAction(intent, v, reasons)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          {/* Temperature pills */}
          <div className="mb-5 flex flex-wrap gap-2">
            {(["all", "hot", "warm", "cold"] as const).map((k) => (
              <button key={k} onClick={() => setFilter(k)}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
                style={{
                  borderColor: filter === k ? "hsl(207 68% 50%)" : cardBorder,
                  background: filter === k ? "hsl(207 68% 50% / 0.1)" : cardBg,
                  color: filter === k ? textHi : textMuted,
                }}>
                {k !== "all" && tempIcon(k)} {k.toUpperCase()}
                {k !== "all" && <span className="opacity-60">· {counts[k]}</span>}
                {k === "all" && <span className="opacity-60">· {visitors.length}</span>}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-sm" style={{ color: textMuted }}>Loading visitors…</p>
          ) : filteredVisitors.length === 0 ? (
            <p className="text-sm" style={{ color: textMuted }}>No visitors yet. They'll appear here in real time.</p>
          ) : (
            <div className="space-y-2">
              {filteredVisitors.map((v) => (
                <button key={v.visitor_id} onClick={() => setSelected(v)}
                  className="w-full text-left rounded-xl border p-4 transition-colors hover:border-[hsl(207_68%_50%/0.4)]"
                  style={{ background: cardBg, borderColor: cardBorder }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full"
                        style={{ background: `${tempColor(v.temperature)}22`, color: tempColor(v.temperature) }}>
                        {tempIcon(v.temperature)}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium truncate" style={{ color: textHi }}>
                          <MapPin size={12} style={{ color: textMuted }} />
                          {[v.city, v.region, v.country].filter(Boolean).join(", ") || "Unknown location"}
                          {v.visit_count >= 2 && (
                            <span className="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5"
                              style={{ background: "hsl(207 68% 50% / 0.15)", color: "hsl(207 68% 75%)" }}>
                              Returning ×{v.visit_count}
                            </span>
                          )}
                        </div>
                        <div className="text-xs flex flex-wrap gap-x-2 mt-0.5" style={{ color: textMuted }}>
                          <span>{v.device || ", "}</span>
                          <span>· {v.total_page_views} pv</span>
                          <span>· score {v.engagement_score}</span>
                          <span>· {timeAgo(v.last_seen)}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={16} style={{ color: textMuted }} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Live feed */}
        <aside>
          <div className="sticky top-4 rounded-xl border" style={{ background: cardBg, borderColor: cardBorder }}>
            <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: cardBorder }}>
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 animate-ping rounded-full" style={{ background: "hsl(140 70% 50%)" }} />
                <span className="relative inline-block h-2 w-2 rounded-full" style={{ background: "hsl(140 70% 50%)" }} />
              </span>
              <h3 className="text-sm font-semibold" style={{ color: textHi }}>Live Activity</h3>
              <Activity size={14} className="ml-auto" style={{ color: textMuted }} />
            </div>
            <div className="flex flex-wrap gap-1 border-b px-3 py-2" style={{ borderColor: cardBorder }}>
              {([
                ["all", "All"],
                ["high_intent", "High Intent"],
                ["bookings", "Bookings"],
                ["calls", "Calls"],
                ["services", "Services"],
                ["returning", "Returning"],
              ] as const).map(([k, lbl]) => (
                <button key={k} onClick={() => setFeedFilter(k)}
                  className="rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors"
                  style={{
                    background: feedFilter === k ? "hsl(207 68% 50% / 0.15)" : "transparent",
                    color: feedFilter === k ? "hsl(207 68% 75%)" : textMuted,
                    border: `1px solid ${feedFilter === k ? "hsl(207 68% 50% / 0.4)" : "transparent"}`,
                  }}>
                  {lbl}
                </button>
              ))}
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y" style={{ borderColor: cardBorder }}>
              {filteredFeed.length === 0 ? (
                <p className="p-4 text-sm" style={{ color: textMuted }}>No matching activity.</p>
              ) : filteredFeed.map((e) => (
                <div key={e.id} className="flex items-start gap-2 px-4 py-2.5">
                  <div className="mt-0.5" style={{ color: "hsl(207 68% 60%)" }}>{eventIcon(e.event_type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs" style={{ color: textHi }}>
                      <span style={{ color: textMuted }}>
                        {[e.city, e.country].filter(Boolean).join(", ") || "Visitor"}
                      </span>{" "}
                      {eventLabel(e).toLowerCase()}{" "}
                      {e.path && <span className="font-mono" style={{ color: textMuted }}>{e.path}</span>}
                    </div>
                    <div className="text-[11px]" style={{ color: textMuted }}>{timeAgo(e.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: cardBorder }}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: textMuted }}>{label}</div>
      <div className="mt-1 text-sm font-medium truncate" style={{ color: textHi }}>{value}</div>
    </div>
  );
}

function SummaryCard({ label, value, icon, accent }: { label: string; value: number; icon: React.ReactNode; accent?: string }) {
  return (
    <div className="rounded-xl border p-3" style={{ background: cardBg, borderColor: cardBorder }}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={{ color: textMuted }}>
        <span style={{ color: accent || "hsl(207 68% 60%)" }}>{icon}</span>
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: accent || textHi }}>{value}</div>
    </div>
  );
}

function Pill({ color, icon, children }: { color: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: `${color}22`, color }}>
      {icon} {children}
    </span>
  );
}
